/* acc_pd_lp_01_01 — Panda Drum landing page 01
 *
 * Progressive enhancement only. With this file blocked the page is still a
 * complete, readable, purchasable landing page: nothing is hidden by CSS until
 * the script has confirmed it is running, and every link is a real anchor.
 *
 * Three things happen here: bands fade in on scroll, the sticky CTA bar shows
 * once the hero button has passed, and the two mobile sliders are built.
 * Everything re-initialises on shopify:section:load for the theme editor.
 */
(function (SECTION_NAME) {
  'use strict';

  /* Fallback only. Each slider declares its own breakpoint in the markup,
     because the two do not share one. */
  var MOBILE_MQ = '(max-width: 767px)';

  /* One record per live section, keyed by section id, so the theme editor can
   * tear one down without touching its siblings. */
  var instances = Object.create(null);

  function error(e) {
    if (window.console && console.warn) console.warn('[' + SECTION_NAME + ']', e);
  }

  /* House throttle / debounce. Anything bound to scroll or resize goes through
   * one of these, never straight to the handler. */
  function throttle(fn, delay, runOnInit, trailing, leading) {
    delay = delay === undefined ? 150 : delay;
    runOnInit = runOnInit === undefined ? false : runOnInit;
    trailing = trailing === undefined ? true : trailing;
    leading = leading === undefined ? true : leading;
    if (!trailing && !leading) return error('throttle: trailing and leading cannot both be false');

    var id, args, context;
    var initArgs = Array.prototype.slice.call(arguments, 5);

    var proc = function () {
      if (!args) return (id = null);
      try { fn.apply(context, args); } catch (e) { error(e); }
      args = null;
      id = setTimeout(proc, delay);
    };

    var b = function () {
      if (id) return (args = arguments);
      context = this;
      try { leading ? fn.apply(context, arguments) : (args = arguments); } catch (e) { error(e); }
      id = setTimeout(trailing ? proc : b.clear, delay);
    };

    b.clear = function () {
      args = null;
      if (!id) return;
      clearTimeout(id);
      id = null;
    };

    if (runOnInit) { try { b.apply(null, initArgs); } catch (e) { error(e); } }
    return b;
  }

  function debounce(fn, delay, runOnInit) {
    delay = delay === undefined ? 150 : delay;
    var id, context;
    var initArgs = Array.prototype.slice.call(arguments, 3);

    var b = function () {
      context = this;
      var a = arguments;
      if (id) clearTimeout(id);
      id = setTimeout(function () {
        id = null;
        try { fn.apply(context, a); } catch (e) { error(e); }
      }, delay);
    };

    b.clear = function () {
      if (!id) return;
      clearTimeout(id);
      id = null;
    };

    if (runOnInit) { try { b.apply(null, initArgs); } catch (e) { error(e); } }
    return b;
  }

  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function onMediaChange(mq, fn) {
    if (mq.addEventListener) { mq.addEventListener('change', fn); return function () { mq.removeEventListener('change', fn); }; }
    mq.addListener(fn);
    return function () { mq.removeListener(fn); };
  }

  /* Swiper is the theme's own assets/swiper.js, a 12.1.2 bundle exposing a
   * global, loaded with defer just before this file. Deferred scripts run in
   * document order so it is normally already there; the poll only covers the
   * theme editor re-injecting the section. */
  function whenSwiper() {
    if (typeof window.Swiper === 'function') return Promise.resolve(window.Swiper);
    return new Promise(function (resolve, reject) {
      var waited = 0;
      var tick = setInterval(function () {
        if (typeof window.Swiper === 'function') { clearInterval(tick); resolve(window.Swiper); }
        else if ((waited += 50) >= 5000) { clearInterval(tick); reject(); }
      }, 50);
    });
  }

  function initReveal(root, store) {
    if (root.getAttribute('data-reveal') !== 'true') return;
    if (!('IntersectionObserver' in window) || reducedMotion()) return;

    var targets = root.querySelectorAll('[data-pd01-reveal]');
    if (!targets.length) return;

    root.classList.add('pd01--js');

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        /* Also reveal anything that has gone ABOVE the viewport. A jump down
         * the page never intersects the bands it skipped, which would leave
         * them invisible if the visitor scrolled back up. */
        var passedAbove = entry.boundingClientRect.top < 0;
        if (!entry.isIntersecting && !passedAbove) return;
        if (passedAbove) entry.target.style.transitionDelay = '0ms';
        entry.target.classList.add('is-in');
        io.unobserve(entry.target);
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -6% 0px' });

    /* Read every rect first, then write. Interleaving them would force a
     * layout per element. */
    var viewportH = window.innerHeight || document.documentElement.clientHeight;
    var pending = [];
    var seen = new Map();

    Array.prototype.forEach.call(targets, function (el) {
      /* Anything on screen at first paint is left alone: starting it at
       * opacity 0 would delay LCP for nothing. */
      if (el.getBoundingClientRect().top < viewportH) return;
      var i = seen.get(el.parentNode) || 0;
      seen.set(el.parentNode, i + 1);
      pending.push([el, i]);
    });

    pending.forEach(function (pair) {
      pair[0].classList.add('pd01-reveal');
      if (pair[1] > 0) pair[0].style.transitionDelay = (Math.min(pair[1], 5) * 90) + 'ms';
      io.observe(pair[0]);
    });

    store.observers.push(io);
  }

  /* Figma 3220:448 desktop / 3220:737 mobile. Visible once the hero CTA has
   * scrolled past, and hidden again over the closing CTA so it never covers
   * the button it duplicates.
   *
   * Two observers rather than a scroll listener, so nothing runs per frame
   * while the visitor scrolls. */
  function initStickyBar(root, store) {
    var bar = root.querySelector('[data-pd01-sticky]');
    if (!bar) return;

    var state = { passedHero: false, atFinal: false };
    var visible = null;

    function apply() {
      var show = state.passedHero && !state.atFinal;
      if (show === visible) return;
      visible = show;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
    }

    var trigger = root.querySelector('[data-pd01-sticky-trigger]');

    if (!trigger || !('IntersectionObserver' in window)) {
      /* Throttled, never bound raw: leading edge so it responds at once, and
         a trailing call so the final position is not missed. */
      var onScroll = throttle(function () {
        state.passedHero = window.pageYOffset > window.innerHeight * 0.6;
        apply();
      }, 150, true);
      window.addEventListener('scroll', onScroll, { passive: true });
      store.teardown.push(function () {
        onScroll.clear();
        window.removeEventListener('scroll', onScroll);
      });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        state.passedHero = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        apply();
      });
    }, { threshold: 0 });
    io.observe(trigger);
    store.observers.push(io);

    var hideAtId = bar.getAttribute('data-pd01-sticky-hide-at');
    var hideAt = hideAtId ? root.querySelector('#' + CSS.escape(hideAtId)) : null;
    if (hideAt) {
      var io2 = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) { state.atFinal = entry.isIntersecting; apply(); });
      }, { threshold: 0, rootMargin: '0px 0px -25% 0px' });
      io2.observe(hideAt);
      store.observers.push(io2);
    }

    apply();
  }

  /* Both sliders are mobile only, so Swiper is built and destroyed as the
   * viewport crosses the breakpoint. Desktop never constructs one. */
  function initSliders(root, store) {
    var nodes = root.querySelectorAll('[data-pd01-swiper]');
    if (!nodes.length) return;

    /* Each slider carries its own breakpoint, because they do not share one:
       the comparison table returns at 768, but the products card table needs
       the full 1200 content width, so it stays a slider until then. */
    Array.prototype.forEach.call(nodes, function (node) {
      initSlider(node, root, store, node.getAttribute('data-pd01-swiper') || MOBILE_MQ);
    });
  }

  function initSlider(node, root, store, query) {
    var mq = window.matchMedia(query);
    var instance = null;

    function build() {
      if (instance) return;
      whenSwiper().then(function (Swiper) {
        if (!mq.matches || !document.body.contains(root) || instance) return;
        node.classList.remove('pd01-swiper--fallback');
        var nav = node.parentElement.querySelector('.pd01-slider-nav');
        instance = new Swiper(node, {
          slidesPerView: 1,
          spaceBetween: 16,
          /* Stops at the first and last slide rather than wrapping. Swiper
             marks the arrows .swiper-button-disabled at each end. */
          loop: false,
          grabCursor: true,
          watchOverflow: true,
          threshold: 8,
          speed: 380,
          a11y: { enabled: true, prevSlideMessage: 'Previous', nextSlideMessage: 'Next' },
          keyboard: { enabled: true, onlyInViewport: true },
          pagination: nav && { el: nav.querySelector('.pd01-slider-nav__rail'), type: 'progressbar' },
          navigation: nav && {
            prevEl: nav.querySelector('.pd01-slider-nav__btn--prev'),
            nextEl: nav.querySelector('.pd01-slider-nav__btn--next')
          }
        });
        store.swipers.push(instance);
      }).catch(function () {
        /* Swiper unavailable: the slides stay a plain stacked list. */
        node.classList.add('pd01-swiper--fallback');
      });
    }

    function destroy() {
      if (!instance) return;
      var i = store.swipers.indexOf(instance);
      if (i > -1) store.swipers.splice(i, 1);
      if (instance.destroy) instance.destroy(true, true);
      instance = null;
    }

    function sync() { if (mq.matches) build(); else destroy(); }
    var syncSoon = debounce(sync, 150);

    sync();
    var off = onMediaChange(mq, syncSoon);
    store.teardown.push(function () { syncSoon.clear(); destroy(); off(); });
  }

  function initRoot(root) {
    if (!root || root.dataset.pd01Ready === 'true') return;
    root.dataset.pd01Ready = 'true';

    var store = { swipers: [], observers: [], teardown: [] };
    instances[root.dataset.pd01SectionId || root.id || 'default'] = { root: root, store: store };

    initReveal(root, store);
    initStickyBar(root, store);
    initSliders(root, store);
  }

  function destroyRoot(key) {
    var entry = instances[key];
    if (!entry) return;
    entry.store.observers.forEach(function (o) { o.disconnect(); });
    entry.store.teardown.forEach(function (fn) { fn(); });
    delete entry.root.dataset.pd01Ready;
    delete instances[key];
  }

  function initAll(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('.' + SECTION_NAME + ' .pd01'), initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.classList.contains(SECTION_NAME)) initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    if (!event.target || !event.target.classList.contains(SECTION_NAME)) return;
    var root = event.target.querySelector('.pd01');
    if (root) destroyRoot(root.dataset.pd01SectionId || root.id || 'default');
  });

  /* Selecting a slide in the theme editor should page the slider to it. */
  document.addEventListener('shopify:block:select', function (event) {
    var slide = event.target && event.target.closest ? event.target.closest('.swiper-slide') : null;
    if (!slide) return;
    var wrapper = slide.parentElement;
    var index = Array.prototype.indexOf.call(wrapper.children, slide);
    Object.keys(instances).forEach(function (key) {
      instances[key].store.swipers.forEach(function (s) {
        if (s.wrapperEl === wrapper) s.slideTo(index);
      });
    });
  });
})('acc_pd_lp_01_01');
